/*
 * Video tickets: the journey from request to launch, and how a launched video
 * is doing (2026-10-22).
 *
 * The journey is read from what the boards and the uploader already record:
 * the video ticket's own dates (made, Copy Ready, Edit Start, Edit Finish),
 * the sync's stage history (when it saw the ticket delivered and ready), and
 * the uploader's launch. A step nobody recorded says so instead of guessing.
 *
 * The performance hover fetches only when opened. Its numbers are Meta's own,
 * which under-report revenue against FunnelFlux, so it says "Meta-reported".
 */
import { useState, type ReactNode } from "react";
import { Group, HoverCard, Loader, Stack, Table, Text } from "@mantine/core";
import { useVideoTicketPerformance } from "../../api/queries";
import type { VideoPerfWindow, VideoSyncTicketRow } from "../../api/client";
import { formatDateTime } from "../../lib/format";

/** "under an hour", "5h", "3d 4h". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return "under an hour";
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest > 0 ? `${days}d ${rest}h` : `${days}d`;
}

export interface JourneyStep {
  label: string;
  at: string | null;
  /** Time since the previous step that has a date; null when either is missing. */
  sincePrevious: string | null;
}

type JourneyRow = Pick<
  VideoSyncTicketRow,
  | "video_created_at"
  | "copy_ready_at"
  | "edit_start_at"
  | "edit_finish_at"
  | "stage_history"
  | "launched_at"
  | "stage"
  | "state_since"
>;

const iso = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

/** The milestones, in order, with the time between the recorded ones. Pure. */
export function journeySteps(row: JourneyRow): JourneyStep[] {
  const history = row.stage_history ?? [];
  const firstAt = (pred: (k: string, stage: number) => boolean): string | null =>
    history.find((h) => pred(h.state_key, h.stage))?.at ?? null;
  const delivered = iso(row.edit_finish_at) ?? firstAt((_k, s) => s >= 2);
  let ready = firstAt((k, s) => s >= 2 && k.includes("|done|"));
  // A ticket the sync met already ready has no history entry for it yet.
  if (!ready && row.stage >= 2 && history.length === 0) ready = iso(row.state_since);
  const points: Array<[string, string | null]> = [
    ["Video ticket made", iso(row.video_created_at)],
    ["Copy ready", iso(row.copy_ready_at)],
    ["Editing started", iso(row.edit_start_at)],
    ["Delivered", delivered],
    ["Ready to launch", ready],
    ["Launched", iso(row.launched_at)],
  ];
  let prev: string | null = null;
  return points.map(([label, at]) => {
    const sincePrevious = at && prev ? formatDuration(Date.parse(at) - Date.parse(prev)) : null;
    if (at) prev = at;
    return { label, at, sincePrevious };
  });
}

/** Request to launch, or request to now while it is not launched. */
export function totalTime(row: JourneyRow, now: number = Date.now()): { text: string; done: boolean } | null {
  const start = iso(row.video_created_at);
  if (!start) return null;
  const end = iso(row.launched_at);
  const ms = (end ? Date.parse(end) : now) - Date.parse(start);
  return { text: formatDuration(ms), done: end !== null };
}

/** The Journey column: launch date and total, with the breakdown on hover. */
export function JourneyCell({ row }: { row: VideoSyncTicketRow }) {
  const total = totalTime(row);
  const steps = journeySteps(row);
  if (!total && !row.launched_at) {
    return (
      <Text component="span" c="dimmed" fz="xs">
        —
      </Text>
    );
  }
  return (
    <HoverCard width={300} shadow="md" withinPortal openDelay={150}>
      <HoverCard.Target>
        <Stack gap={0} style={{ cursor: "default" }} tabIndex={0} aria-label="Journey breakdown">
          {row.launched_at ? (
            <Text fz="xs">Launched {formatDateTime(row.launched_at)}</Text>
          ) : null}
          {total && (
            <Text fz="xs" c="dimmed">
              {total.done ? `${total.text} from request to launch` : `${total.text} so far`}
            </Text>
          )}
        </Stack>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap={6}>
          <Text fz="xs" fw={600}>
            Request to launch
          </Text>
          {steps.map((s) => (
            <Group key={s.label} justify="space-between" wrap="nowrap" gap="xs">
              <Stack gap={0}>
                <Text fz="xs">{s.label}</Text>
                <Text fz="xs" c="dimmed">
                  {s.at ? formatDateTime(s.at) : "not recorded"}
                </Text>
              </Stack>
              <Text fz="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                {s.sincePrevious ? `+${s.sincePrevious}` : ""}
              </Text>
            </Group>
          ))}
          {row.editor && (
            <Text fz="xs" c="dimmed">
              Edited by {row.editor}
            </Text>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

const money = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const WINDOWS: Array<[string, "today" | "yesterday" | "last_3d" | "last_7d"]> = [
  ["Today", "today"],
  ["Yesterday", "yesterday"],
  ["Last 3 days", "last_3d"],
  ["Last 7 days", "last_7d"],
];

/** The numbers for one window, as a table row's cells. */
function windowCells(w: VideoPerfWindow | null | undefined): ReactNode {
  if (!w) {
    return (
      <Table.Td colSpan={4}>
        <Text fz="xs" c="dimmed">
          no data
        </Text>
      </Table.Td>
    );
  }
  return (
    <>
      <Table.Td>{money(w.spend)}</Table.Td>
      <Table.Td>{w.purchases}</Table.Td>
      <Table.Td>{money(w.cpa)}</Table.Td>
      <Table.Td>{w.roas === null ? "—" : `${w.roas}x`}</Table.Td>
    </>
  );
}

/** Wraps a launched video's name: hovering shows how it is doing. */
export function PerformanceHover({ row, children }: { row: VideoSyncTicketRow; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // The stored days come back at once; today is a slow live read, so it is
  // asked for separately and fills in its own row when it arrives.
  const perf = useVideoTicketPerformance(row.id, open);
  const live = useVideoTicketPerformance(row.id, open, true);
  if (row.launched_adset_count < 1) return <>{children}</>;
  return (
    <HoverCard width={380} shadow="md" withinPortal openDelay={200} onOpen={() => setOpen(true)}>
      <HoverCard.Target>
        <span>{children}</span>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap={6}>
          <Text fz="xs" fw={600}>
            Performance · {row.launched_adset_count} ad set{row.launched_adset_count === 1 ? "" : "s"} · Meta-reported
          </Text>
          {perf.isLoading ? (
            <Group gap="xs">
              <Loader size="xs" />
              <Text fz="xs" c="dimmed">
                Loading…
              </Text>
            </Group>
          ) : perf.isError || !perf.data ? (
            <Text fz="xs" c="red">
              Couldn't load the numbers.
            </Text>
          ) : (
            <>
              <Table fz="xs" verticalSpacing={2} horizontalSpacing={6} style={{ fontVariantNumeric: "tabular-nums" }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>Spend</Table.Th>
                    <Table.Th>Purch.</Table.Th>
                    <Table.Th>CPA</Table.Th>
                    <Table.Th>ROAS</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td>Today</Table.Td>
                    {live.isLoading ? (
                      <Table.Td colSpan={4}>
                        <Text fz="xs" c="dimmed">
                          loading…
                        </Text>
                      </Table.Td>
                    ) : (
                      windowCells(live.data?.today)
                    )}
                  </Table.Tr>
                  {WINDOWS.filter(([, key]) => key !== "today").map(([label, key]) => (
                    <Table.Tr key={key}>
                      <Table.Td>{label}</Table.Td>
                      {windowCells(perf.data[key])}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {live.data?.today_error && (
                <Text fz="xs" c="dimmed">
                  {live.data.today_error}
                </Text>
              )}
              {live.isError && (
                <Text fz="xs" c="dimmed">
                  Today's numbers could not be read.
                </Text>
              )}
              <Text fz="xs" c="dimmed">
                Meta under-reports revenue against FunnelFlux, so ROAS here reads low.
              </Text>
            </>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
